import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { formatDateTime, formatDayLabel } from '@/lib/dates'
import { cn } from '@/lib/utils'
import { useClientDashboard } from '@/queries/clientDashboards'
import { useClientEmails } from '@/queries/clientEmails'
import { useClientMeetings, useFetchClientMeetings } from '@/queries/clientMeetings'
import type { ClientEmail } from '@/types/clientEmails'
import type { CallRequest } from '@/types/clientMeetings'
import { AssignMeetingDialog, needsAssigningLabel } from './AssignMeetingDialog'
import { CadenceEditor } from './CadenceEditor'
import { DashCard } from './dashboardParts'
import { EmailPanel } from './EmailPanel'
import { LoadError } from './LoadError'
import { MeetingPanel } from './MeetingPanel'
import { NextCallCard } from './NextCallCard'

function FirefliesSource() {
  return (
    <span
      data-testid="fireflies-source"
      className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-etn-pill bg-etn-neutral-subtle py-px pl-0.5 pr-2 text-[11px] font-semibold text-etn-text-label"
    >
      <i aria-hidden className="grid h-[15px] w-[15px] place-items-center rounded-full bg-etn-info-text text-[8.5px] font-bold not-italic text-white">
        F
      </i>
      Fireflies
    </span>
  )
}

const notLoggedCount = ({ requests }: { requests: CallRequest[] }) => requests.filter(request => !request.link && !request.dismissed).length

type Selection = { kind: 'call' | 'email'; id: string }

function ListButton({
  selected,
  day,
  title,
  waiting,
  onClick,
}: {
  selected: boolean
  day: string
  title: string
  waiting: number
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'block w-full border-b border-[var(--etn-gray-100)] px-[18px] py-2.5 text-left last:border-b-0 hover:bg-etn-row-hover',
        selected && 'bg-etn-accent-tint'
      )}
    >
      <span className="block text-[13px] font-semibold text-etn-text">{day}</span>
      <span className="block truncate text-[12px] text-etn-text-soft">{title}</span>
      {waiting > 0 && (
        <Badge variant="outline" className="mt-1">
          {waiting} not logged
        </Badge>
      )}
    </button>
  )
}

function EmailList({
  emails,
  failed,
  onRetry,
  selectedId,
  onSelect,
}: {
  emails: ClientEmail[] | undefined
  failed: boolean
  onRetry: () => void
  selectedId: string | null
  onSelect: (id: string) => void
}) {
  return (
    <div data-testid="email-list">
      <DashCard title="Emails" source="email">
        {failed ? (
          <div className="flex flex-wrap items-center justify-center gap-2 px-[18px] py-6 text-center text-[13px] text-etn-text-soft">
            <span>Emails didn't load</span>
            <Button size="xs" variant="outline" onClick={onRetry}>
              Try again
            </Button>
          </div>
        ) : !emails ? null : emails.length ? (
          <ul>
            {emails.map(email => (
              <li key={email._id}>
                <ListButton
                  selected={selectedId === email._id}
                  day={formatDayLabel(email.date)}
                  title={email.subject ?? 'No subject'}
                  waiting={notLoggedCount(email)}
                  onClick={() => onSelect(email._id)}
                />
              </li>
            ))}
          </ul>
        ) : (
          <div className="px-[18px] py-6 text-center">
            <p className="text-[13px] font-medium text-etn-text">No logged emails</p>
            <p className="mt-1 text-[12px] text-etn-text-soft">Use Log email in the header.</p>
          </div>
        )}
      </DashCard>
    </div>
  )
}

export function MeetingsView({
  companyId,
  initialMeetingId,
  initialEmailId,
  highlightRequestId,
}: {
  companyId: string
  initialMeetingId?: string
  initialEmailId?: string
  highlightRequestId?: string
}) {
  const meetings = useClientMeetings(companyId)
  const dashboard = useClientDashboard(companyId).data?.data
  const fetchNew = useFetchClientMeetings(companyId)
  const emailsQuery = useClientEmails(companyId)
  const emails = emailsQuery.data?.data
  const [selection, setSelection] = useState<Selection | null>(
    initialEmailId ? { kind: 'email', id: initialEmailId } : initialMeetingId ? { kind: 'call', id: initialMeetingId } : null
  )
  const [assigning, setAssigning] = useState(false)

  if (meetings.isError) return <LoadError title="Meetings didn't load" onRetry={() => meetings.refetch()} />
  const data = meetings.data?.data
  if (!data || !dashboard) return null

  const showingEmail = selection?.kind === 'email'
  const selectedEmail = showingEmail ? emails?.find(email => email._id === selection.id) : undefined
  // An email selection never falls back to a call, even while the emails load.
  const selected = showingEmail ? undefined : (data.meetings.find(meeting => meeting._id === selection?.id) ?? data.meetings[0])
  const emailGone = showingEmail && Boolean(emails) && !selectedEmail
  const highlightFor = (id: string) => (id === initialMeetingId || id === initialEmailId ? highlightRequestId : undefined)
  const fetchRow = (
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--etn-gray-100)] px-[18px] py-2">
      <span className="text-[12px] text-etn-text-soft">{data.lastCheckedAt ? `Last checked ${formatDateTime(data.lastCheckedAt)}` : 'Not checked yet'}</span>
      <Button size="xs" variant="outline" disabled={fetchNew.isPending} onClick={() => fetchNew.mutate()}>
        Fetch new from Fireflies
      </Button>
    </div>
  )

  return (
    <div className="space-y-4">
      {data.waiting.length > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-etn-card border border-etn-border-hairline bg-etn-accent-tint px-[18px] py-2.5">
          <span className="text-[13px] text-etn-text">Some Fireflies calls match more than one client.</span>
          <Button size="sm" onClick={() => setAssigning(true)}>
            {needsAssigningLabel(data.waiting.length)}
          </Button>
          <AssignMeetingDialog meetings={data.waiting} allowDismiss={false} open={assigning} onOpenChange={setAssigning} />
        </div>
      )}
      <div className="grid items-start gap-4 lg:grid-cols-[260px_minmax(0,1fr)]">
        <div className="space-y-4">
          <NextCallCard dashboard={dashboard} />
          <CadenceEditor key={`cadence-${companyId}`} dashboard={dashboard} />
          <div data-testid="call-list">
            <DashCard title="Past calls" right={<FirefliesSource />} source="call">
              {fetchRow}
              <ul>
                {data.meetings.map(meeting => (
                  <li key={meeting._id}>
                    <ListButton
                      selected={selected?._id === meeting._id}
                      day={formatDayLabel(meeting.date)}
                      title={meeting.title}
                      waiting={notLoggedCount(meeting)}
                      onClick={() => setSelection({ kind: 'call', id: meeting._id })}
                    />
                  </li>
                ))}
              </ul>
            </DashCard>
          </div>
          <EmailList
            emails={emails}
            failed={emailsQuery.isError}
            onRetry={() => emailsQuery.refetch()}
            selectedId={selectedEmail?._id ?? null}
            onSelect={id => setSelection({ kind: 'email', id })}
          />
        </div>
        {emailGone ? (
          <DashCard title="Email" source="email">
            <div className="px-[18px] py-8 text-center">
              <p className="font-medium text-etn-text">This email was deleted</p>
              <p className="mt-1 text-[13px] text-etn-text-soft">Tickets and Linear issues created from it are still there.</p>
            </div>
          </DashCard>
        ) : selectedEmail ? (
          <EmailPanel
            key={selectedEmail._id}
            email={selectedEmail}
            companyId={companyId}
            companyName={dashboard.companyName}
            trackerEnabled={dashboard.clientTrackerEnabled}
            highlightRequestId={highlightFor(selectedEmail._id)}
          />
        ) : showingEmail ? null : selected ? (
          <MeetingPanel
            key={selected._id}
            meeting={selected}
            companyId={companyId}
            companyName={dashboard.companyName}
            trackerEnabled={dashboard.clientTrackerEnabled}
            highlightRequestId={highlightFor(selected._id)}
          />
        ) : (
          <DashCard title="Meetings">
            <div className="px-[18px] py-8 text-center">
              <p className="font-medium text-etn-text">No calls with {dashboard.companyName} in the last six months</p>
              <p className="mt-1 text-[13px] text-etn-text-soft">
                Fireflies calls with {dashboard.companyName}'s people appear here within 15 minutes.
              </p>
            </div>
          </DashCard>
        )}
      </div>
    </div>
  )
}
